import React, { useState, useMemo, useEffect } from 'react';
import Pagination from '@components/Layout/Pagination';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReportSubmitPage from './ReportSubmitPage';
import NurseAssignedDeviceList from './NurseAssignedDeviceList';
import HospitalDashboardAnalytics from './HospitalDashboardAnalytics';
import { ASSIGN_PATH } from '@constants/routes';
import Analyticschart from './AnalyticsChart';
import { HOSPITAL, NURSE } from '@constants/constants';
import { getUserAttributes } from '@utils/authUtils';
import { USER_ROLE } from '@constants/cognitoAttributes';
import AssignModalPopUp from './AssignModalPopUp';
import { hospitalUuid } from '@utils/hospitalUtils';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import Swal from 'sweetalert2';
import DataLoader from './DataLoader';
import { setIsToggleButtonActive } from '@store/reducers/userSlice';

const MySwal = Swal;

interface Nurse {
    tenant_id: string;
    user_id: string;
    nurse_id?: string;
    name: string;
    uuid: string;
}

interface Device {
    uuid: string;
    device_id: string;
    device_name?: string;
    hospital_id: string;
}

const AnalyticsAndReportBtn = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isAnalyticsOn, setIsAnalyticsOn] = useState(true);
    const [nurseData, setNurseData] = useState<Nurse[]>([]);
    const [deviceData, setDeviceData] = useState<Device[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [userRole, setUserRole] = useState('');
    const [loading, setLoading] = useState(false);
    const isToggleButtonActive = useAppSelector(
        (state) => state.user.isToggleButtonActive
    );
    const dispatch = useAppDispatch();
    const handleToggle = (mode: string) => {
        if (mode === 'analytics') {
            setIsAnalyticsOn(true);
            dispatch(setIsToggleButtonActive(false));
        } else {
            setIsAnalyticsOn(false);
            dispatch(setIsToggleButtonActive(true));
        }
    };
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const loggedInUserRole = useAppSelector(
        (state) => state.login.loggedInUserRole
    );

    const fetchData = async () => {
        try {
            const data = await hospitalUuid(loggedInUserTenantID);
            if (data?.nurseList && data?.deviceList) {
                setNurseData(data?.nurseList);
                setDeviceData(data?.deviceList);
            }
            // setUserData(data);
        } catch (error: any) {
            let errorMessage = 'Unexpected error';

            switch (error.name) {
                case 'ResourceNotFoundException':
                    errorMessage = 'Error: Table or item not found.';
                    break;
                case 'InternalServerError':
                    errorMessage = 'Error: Internal server error.';
                    break;
                case 'ProvisionedThroughputExceededException':
                    errorMessage =
                        'Error: Provisioned throughput exceeded. Please try again later.';
                    break;
                case 'LimitExceededException':
                    errorMessage =
                        'Error: Limit exceeded. Please try again later.';
                    break;
                default:
                    errorMessage = error.message;
                    break;
            }
            MySwal.fire({
                position: 'top-end', // Position the toast in the top-right corner
                icon: 'error',
                title: errorMessage,
                showConfirmButton: false,
                timer: 3000,
                toast: true,
                customClass: {
                    popup: '!text-red !bg-light_red', // Add custom class to the popup
                },
                width: 'auto',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (loggedInUserRole != '') {
            setUserRole(loggedInUserRole);
        }
        if (isToggleButtonActive == true) {
            setIsAnalyticsOn(false);
        }
    }, [loggedInUserRole, isToggleButtonActive]);
    // const handleAssigned = () => {
    //     setShowModal(true);
    // };

    async function assign() {
        try {
            setLoading(true);
            await fetchData();
        } catch (error: any) {
            console.log('err', error);
        } finally {
            setShowModal(true);
            setLoading(false);
        }
    }

    return (
        <>
            <div style={{ width: '100%', maxWidth: '100%' }}>
                {/* ── Dashboard Header Row ── */}
                {isAnalyticsOn ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 4,
                        }}
                    >
                        {/* Left: Title + Tab Toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                            <h1
                                style={{
                                    fontSize: 24,
                                    fontWeight: 700,
                                    color: '#0F172A',
                                    margin: 0,
                                    letterSpacing: '-0.3px',
                                }}
                            >
                                Dashboard
                            </h1>

                            {/* Analytics / Report toggle */}
                            <div
                                style={{
                                    display: 'flex',
                                    background: '#EFF6FF',
                                    borderRadius: 8,
                                    padding: 3,
                                    gap: 2,
                                }}
                            >
                                <button
                                    onClick={() => handleToggle('analytics')}
                                    style={{
                                        padding: '6px 20px',
                                        borderRadius: 6,
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        background: isAnalyticsOn ? '#2563EB' : 'transparent',
                                        color: isAnalyticsOn ? '#fff' : '#64748B',
                                        transition: 'all 0.2s',
                                        boxShadow: isAnalyticsOn ? '0 2px 8px rgba(37,99,235,0.25)' : 'none',
                                    }}
                                >
                                    {t('analytics.analytics')}
                                </button>
                                <button
                                    onClick={() => handleToggle('report')}
                                    style={{
                                        padding: '6px 20px',
                                        borderRadius: 6,
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        background: !isAnalyticsOn ? '#2563EB' : 'transparent',
                                        color: !isAnalyticsOn ? '#fff' : '#64748B',
                                        transition: 'all 0.2s',
                                        boxShadow: !isAnalyticsOn ? '0 2px 8px rgba(37,99,235,0.25)' : 'none',
                                    }}
                                >
                                    {t('analytics.report')}
                                </button>
                            </div>
                        </div>

                        {/* Right: Assign Device Button */}
                        {userRole.toLowerCase() === HOSPITAL.toLowerCase() && (
                            <button
                                onClick={assign}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 10,
                                    padding: '10px 20px',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.35)',
                                    transition: 'all 0.2s',
                                    letterSpacing: '0.2px',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 6px 18px rgba(37,99,235,0.45)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(37,99,235,0.35)';
                                }}
                            >
                                <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 400 }}>+</span>
                                {t('analytics.assign')}
                            </button>
                        )}
                    </div>
                ) : (
                    /* ── Reports Banner Row ── */
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            marginBottom: 20,
                        }}
                    >
                        {/* Left: Title + Subtext + Toggle */}
                        <div style={{ flex: 1 }}>
                            <h1
                                style={{
                                    fontSize: 26,
                                    fontWeight: 700,
                                    color: '#0F172A',
                                    margin: 0,
                                    letterSpacing: '-0.3px',
                                }}
                            >
                                Reports
                            </h1>
                            <p style={{ fontSize: 14, color: '#64748B', margin: '4px 0 16px 0', fontWeight: 500 }}>
                                Generate and download device and patient reports with detailed insights.
                            </p>

                            {/* Analytics / Report toggle */}
                            <div
                                style={{
                                    display: 'inline-flex',
                                    background: '#EFF6FF',
                                    borderRadius: 8,
                                    padding: 3,
                                    gap: 2,
                                }}
                            >
                                <button
                                    onClick={() => handleToggle('analytics')}
                                    style={{
                                        padding: '6px 20px',
                                        borderRadius: 6,
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        background: 'transparent',
                                        color: '#64748B',
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    {t('analytics.analytics')}
                                </button>
                                <button
                                    onClick={() => handleToggle('report')}
                                    style={{
                                        padding: '6px 20px',
                                        borderRadius: 6,
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        background: '#2563EB',
                                        color: '#fff',
                                        transition: 'all 0.2s',
                                        boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
                                    }}
                                >
                                    {t('analytics.report')}
                                </button>
                            </div>
                        </div>

                        {/* Right: Beautiful Premium Clipboard Folder SVG Banner Graphic */}
                        <div style={{ flexShrink: 0, display: 'block' }}>
                            <svg width="220" height="130" viewBox="0 0 240 150" fill="none" xmlns="http://www.w3.org/2000/svg">
                                {/* Grid dots */}
                                <circle cx="210" cy="30" r="2" fill="#E2E8F0" />
                                <circle cx="222" cy="30" r="2" fill="#E2E8F0" />
                                <circle cx="234" cy="30" r="2" fill="#E2E8F0" />
                                <circle cx="210" cy="42" r="2" fill="#E2E8F0" />
                                <circle cx="222" cy="42" r="2" fill="#E2E8F0" />
                                <circle cx="234" cy="42" r="2" fill="#E2E8F0" />
                                <circle cx="210" cy="54" r="2" fill="#E2E8F0" />
                                <circle cx="222" cy="54" r="2" fill="#E2E8F0" />
                                <circle cx="234" cy="54" r="2" fill="#E2E8F0" />

                                {/* Clipboard tablet */}
                                <rect x="90" y="20" width="70" height="100" rx="10" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="2" />
                                <rect x="110" y="14" width="30" height="8" rx="4" fill="#3B82F6" />
                                {/* Tablet details */}
                                <circle cx="108" cy="40" r="10" fill="#EFF6FF" />
                                <path d="M104 40 L107 43 L112 37" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <rect x="100" y="66" width="50" height="4" rx="2" fill="#E2E8F0" />
                                <rect x="100" y="76" width="35" height="4" rx="2" fill="#E2E8F0" />
                                {/* Tiny bar charts */}
                                <rect x="100" y="90" width="8" height="20" rx="2" fill="#93C5FD" />
                                <rect x="112" y="85" width="8" height="25" rx="2" fill="#60A5FA" />
                                <rect x="124" y="80" width="8" height="30" rx="2" fill="#2563EB" />

                                {/* Blue folder with down arrow */}
                                <path d="M40 60C40 56.6863 42.6863 54 46 54H68L76 64H114C117.314 64 120 66.6863 120 70V122C120 125.314 117.314 128 114 128H46C42.6863 128 40 125.314 40 122V60Z" fill="#2563EB" />
                                {/* Down arrow */}
                                <circle cx="80" cy="96" r="14" fill="#FFFFFF" opacity="0.15" />
                                <path d="M80 86 L80 102 M74 96 L80 102 L86 96" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M70 106 L90 106" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />

                                {/* Decorative leaves */}
                                <path d="M165 95C165 95 180 85 185 100C180 105 165 95 165 95Z" fill="#4ADE80" opacity="0.4" />
                                <path d="M75 135C75 135 60 140 65 125C70 125 75 135 75 135Z" fill="#4ADE80" opacity="0.4" />
                            </svg>
                        </div>
                    </div>
                )}

                {/* ── Content ── */}
                {isAnalyticsOn ? (
                    <>
                        {userRole.toLowerCase() === HOSPITAL.toLowerCase() && (
                            <HospitalDashboardAnalytics />
                        )}
                        <Analyticschart />
                    </>
                ) : (
                    <ReportSubmitPage />
                )}

                {isAnalyticsOn && userRole.toLowerCase() === NURSE.toLowerCase() && (
                    <div>
                        <h1 className="text-sm font-semibold text-black ml-[4rem] mt-4 mb-3">
                            {t('analytics.assigned_devices')}
                        </h1>
                        <div className="bg-white flex flex-col rounded-2xl mt-4 ml-14 h-[29rem] w-[28rem] relative">
                            <NurseAssignedDeviceList />
                        </div>
                    </div>
                )}

                {showModal && (
                    <>
                        <AssignModalPopUp
                            showModal={showModal}
                            onClose={() => setShowModal(false)}
                            deviceData={deviceData}
                            nurseData={nurseData}
                        />
                    </>
                )}
            </div>
            {loading && <DataLoader />}
        </>
    );
};
export default AnalyticsAndReportBtn;
